import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { BlogPost, CreatePostInput, UpdatePostInput } from '../models/Post';
import { blogService } from '../services/blogService';

interface BlogContextType {
    posts: BlogPost[];
    loading: boolean;
    error: string | null;
    createPost: (post: CreatePostInput) => Promise<void>;
    updatePost: (id: string, updates: UpdatePostInput) => Promise<void>;
    deletePost: (id: string) => Promise<void>;
    reorderPosts: (postIds: string[]) => Promise<void>;
    refreshPosts: () => Promise<void>;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

export function BlogProvider({ children }: { children: ReactNode }) {
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refreshPosts = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('BlogContext: Fetching posts');
            const fetchedPosts = await blogService.getPosts();
            console.log('BlogContext: Received posts:', fetchedPosts);
            setPosts(fetchedPosts.posts);
        } catch (error: any) {
            console.error('BlogContext: Error fetching posts:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshPosts();
    }, []);

    const createPost = async (postData: CreatePostInput) => {
        try {
            setLoading(true);
            setError(null);
            console.log('BlogContext: Creating post with data:', postData);
            const newPost = await blogService.createPost(postData);
            console.log('BlogContext: Created post:', newPost);
            await refreshPosts();
        } catch (error: any) {
            console.error('BlogContext: Error creating post:', error);
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updatePost = async (id: string, updates: UpdatePostInput) => {
        try {
            setLoading(true);
            setError(null);
            
            // Remove DownloadableFile from updates if it's null or undefined
            const { DownloadableFile, ...updatesWithoutFile } = updates;
            const finalUpdates = DownloadableFile ? updates : updatesWithoutFile;
            
            console.log('BlogContext: Updating with final payload:', finalUpdates);
            await blogService.updatePost(id, finalUpdates);
            await refreshPosts();
        } catch (error: any) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const deletePost = async (id: string) => {
        try {
            setLoading(true);
            setError(null);
            await blogService.deletePost(id);
            await refreshPosts();
        } catch (error: any) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const reorderPosts = async (postIds: string[]) => {
        try {
            setLoading(true);
            setError(null);
            await blogService.reorderPosts(postIds);
            await refreshPosts();
        } catch (error: any) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return (
        <BlogContext.Provider value={{ 
            posts, 
            loading, 
            error, 
            createPost, 
            updatePost, 
            deletePost, 
            reorderPosts,
            refreshPosts 
        }}>
            {children}
        </BlogContext.Provider>
    );
}

export function useBlog() {
    const context = useContext(BlogContext);
    if (context === undefined) {
        throw new Error('useBlog must be used within a BlogProvider');
    }
    return context;
} 